<template>
  <div class="invoice-view">
    <v-card flat>
      <ReportViewBar
        :title="`Sale Summary`"
        :location="reportsData.location"
        :start="dateTime.start"
        :end="dateTime.end"
      />
      <v-card-text>
        <v-layout row wrap class="no-print">
          <v-spacer></v-spacer>
          <v-flex xs12 md3>
            <DateTimePicker
              :range="true"
              :defaultRange="selectedRange"
              v-model="dateTime"
              :initStartDate="start"
              :initEndDate="end"
              @submit="loadReport()"
            />
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-progress-linear v-if="loading" :indeterminate="true"></v-progress-linear>
      <div v-else>
        <v-container fluid>
          <v-card dark>
            <v-card-text>
              <div class="text-xs-center title">Summary</div>
            </v-card-text>
          </v-card>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap>
                <v-flex>
                  <v-layout column align-center>
                    <v-flex>
                      <img :width="iconSize" src="@/assets/icons/home/checkBook.png" />
                    </v-flex>
                    <v-flex>
                      <div class="font-weight-bold">{{ $t('labels.netSales') }}</div>
                    </v-flex>
                    <v-flex class="title font-weight-light mt-2 pink--text">
                      {{ reportsData.saleSummary.netSales | currency }}
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex>
                  <v-layout column align-center>
                    <v-flex>
                      <img :width="iconSize" src="@/assets/icons/home/bag.png" />
                    </v-flex>
                    <v-flex>
                      <div class="font-weight-bold">{{ $t('labels.taxCollected') }}</div>
                    </v-flex>
                    <v-flex class="title font-weight-light mt-2 pink--text">
                      {{ reportsData.saleSummary.taxCollected | currency }}
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex>
                  <v-layout column align-center>
                    <v-flex>
                      <img :width="iconSize" src="@/assets/icons/home/revenue.png" />
                    </v-flex>
                    <v-flex>
                      <div class="font-weight-bold">{{ $t('labels.revenue') }}</div>
                    </v-flex>
                    <v-flex class="title font-weight-light mt-2 pink--text">
                      {{ reportsData.saleSummary.revenue | currency }}
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex>
                  <v-layout column align-center>
                    <v-flex>
                      <img :width="iconSize" src="@/assets/icons/home/expense.png" />
                    </v-flex>
                    <v-flex>
                      <div class="font-weight-bold">{{ $t('labels.expense') }}</div>
                    </v-flex>
                    <v-flex class="title font-weight-light mt-2 pink--text">
                      {{ reportsData.profitAndLoss.report.Expense.total | currency }}
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex>
                  <v-layout column align-center>
                    <v-flex>
                      <img :width="iconSize" src="@/assets/icons/home/coins.png" />
                    </v-flex>
                    <v-flex>
                      <div class="font-weight-bold">{{ $t('labels.discounts') }}</div>
                    </v-flex>
                    <v-flex class="title font-weight-light mt-2 pink--text">
                      {{ reportsData.saleSummary.discounts | currency }}
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>

          <v-card dark>
            <v-card-text>
              <div class="text-xs-center title">Sale Detail</div>
            </v-card-text>
          </v-card>

          <!-- Sale By Category -->
           <v-expansion-panel :expand="expand" class="elevation-0 mt-5">
            <v-expansion-panel-content lazy>
              <template v-slot:header>
                <v-layout row wrap>
                  <v-flex>
                    <h2 class="headline font-weight-regular">
                      Sale By Category
                      <span>({{ reportsData.saleByCategory.length }})</span>
                    </h2>
                  </v-flex>
                  <v-spacer></v-spacer>
                  <v-flex class="text-xs-right mr-2" shrink>
                    <h2 class="headline font-weight-regular">
                      {{ categoryAmountTotal | currency }}
                    </h2>
                  </v-flex>
                </v-layout>
              </template>

              <table class="center">
                <tbody class="report-container">
                  <v-card-text class="pt-0">
                    <v-data-table
                      :headers="headers"
                      :items="reportsData.saleByCategory"
                      hide-actions
                    >
                      <template slot="items" slot-scope="props">
                        <td class="text-xs-left">
                          {{ props.item.name || 'Uncategorized' }}
                        </td>
                        <td class="text-xs-center">
                          {{ parseFloat(props.item.quantity).toFixed() }}
                        </td>
                        <td data-cy="expenseComponentsListPayeeName" class="text-xs-left">
                          {{ props.item.total | currency }}
                        </td>
                      </template>
                      <template slot="footer">
                        <tr>
                          <td :colspan="headers && headers.length - 2">
                            <div class="text-xs-right font-weight-bold">
                              {{ $t('labels.quantity') }}: {{ categoryQuantityTotal }}
                            </div>
                          </td>
                          <td class="text-xs-right font-weight-bold">
                            {{ $t('labels.total') }}: {{ categoryAmountTotal | currency }}
                          </td>
                          <td></td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </tbody>
              </table>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-divider class="my-2"></v-divider>

          <!-- income -->
          <v-expansion-panel expand class="elevation-0">
            <v-expansion-panel-content>
              <template v-slot:header>
                <v-layout row wrap>
                  <v-flex>
                    <h2 class="headline font-weight-regular">Income</h2>
                  </v-flex>
                  <v-spacer> </v-spacer>
                  <v-flex class="text-xs-right mr-2" shrink>
                    <h2 class="headline font-weight-regular">
                      {{ incomeTotal | currency }}
                    </h2>
                  </v-flex>
                </v-layout>
              </template>
              <v-data-table
                :headers="incomeHeaders"
                hide-actions
                class="px-4"
                :items="income.InvoiceDetailTypes"
                expand
                item-key="id"
              >
                <template v-slot:items="props">
                  <tr>
                    <td class="text-left">
                      <v-btn
                        class="no-print"
                        @click="props.expanded = !props.expanded"
                        icon
                        v-if="!props.expanded"
                      >
                        <v-icon>mdi-chevron-down</v-icon>
                      </v-btn>
                      <v-btn
                        class="no-print"
                        @click="props.expanded = !props.expanded"
                        icon
                        v-if="props.expanded"
                      >
                        <v-icon>mdi-chevron-up</v-icon>
                      </v-btn>
                      <span>{{ props.item.name }}</span>
                    </td>

                    <td class="text-xs-right">
                      {{ props.item.revenue | currency }}
                    </td>
                  </tr>
                </template>
                <template v-slot:expand="props">
                  <Category :props="props" :date="getDateTime" :LocationId="locationId" />
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- cost of goods -->
          <v-divider class="my-2"> </v-divider>
          <v-expansion-panel expand class="elevation-0">
            <v-expansion-panel-content>
              <template v-slot:header>
                <v-layout row wrap>
                  <v-flex>
                    <h2 class="headline font-weight-regular">Cost Of Goods</h2>
                  </v-flex>
                  <v-spacer> </v-spacer>
                  <v-flex class="text-xs-right mr-2" shrink>
                    <h2 class="headline font-weight-regular">
                      {{ costTotal | currency }}
                    </h2>
                  </v-flex>
                </v-layout>
              </template>
              <v-data-table
                :headers="incomeHeaders"
                hide-actions
                class="px-4"
                :items="income.InvoiceDetailTypes"
                expand
                item-key="id"
              >
                <template v-slot:items="props">
                  <tr>
                    <td class="text-left">
                      <v-btn
                        class="no-print"
                        @click="props.expanded = !props.expanded"
                        icon
                        v-if="!props.expanded"
                      >
                        <v-icon>mdi-chevron-down</v-icon>
                      </v-btn>
                      <v-btn
                        class="no-print"
                        @click="props.expanded = !props.expanded"
                        icon
                        v-if="props.expanded"
                      >
                        <v-icon>mdi-chevron-up</v-icon>
                      </v-btn>
                      <span>{{ props.item.name }}</span>
                    </td>

                    <td class="text-xs-right">
                      {{ props.item.cost | currency }}
                    </td>
                  </tr>
                </template>
                <template v-slot:expand="props">
                  <Category :props="props" :LocationId="locationId" />
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- <v-expansion-panel focusable :expand="expand" popout class="elevation-0">
            <v-expansion-panel-content
              lazy
              v-for="(item, i) in goodsIncome.data"
              :key="i"
            >
              <template v-slot:header>
                <v-layout row wrap>
                  <v-flex>
                    <h2 class="title font-weight-regular">
                      {{ i }}
                    </h2>
                  </v-flex>
                  <v-spacer></v-spacer>
                  <v-flex class="text-xs-right mr-2" shrink>
                    <h2 class="title font-weight-regular">
                      {{ item.total | currency }}
                    </h2>
                  </v-flex>
                </v-layout>
              </template>

              <v-divider></v-divider>

              <v-layout column pa-3>
                <table class="center">
                  <tbody class="report-container">
                    <v-card-text class="pt-0">
                      <v-data-table
                        :headers="saleHeaders"
                        :items="Object.entries(item.data)"
                        hide-actions
                      >
                        <template slot="items" slot-scope="props">
                          <td class="text-xs-left">
                            
                            {{ productName(Object.keys(props.item[1].data)) || 'item' }}
                          </td>
                          <td class="text-xs-left">
                            {{ props.item[0] }}
                          </td>
                          <td class="text-xs-center">
                            {{
                              Object.values(props.item[1].data)[0] &&
                              Object.values(props.item[1].data)[0].quantity
                            }}
                          </td>
                          <td class="text-xs-left">
                            {{ props.item[1].cost | currency }}
                          </td>
                          <td class="text-xs-left">
                            {{ props.item[1].total | currency }}
                          </td>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </tbody>
                </table>
              </v-layout>
            </v-expansion-panel-content>
          </v-expansion-panel> -->
        </v-container>
      </div>
    </v-card>
  </div>
</template>

<script>
import ReportViewBar from '@/components/ReportViewBar'
import DateTimePicker from '@/components/DateTimePicker'
import reportService from '../../../Reports/service'
import Category from './components/Category'
import invoiceService from '../../../Invoice/service'

export default {
  data() {
    return {
      subCategory: [],
      incomeHeaders: [
        {
          text: this.$t('labels.category'),
          align: 'left',
          sortable: false,
          value: 'MasterCategories.name',
        },
        {
          text: this.$t('labels.revenue'),
          align: 'right',
          sortable: false,
          value: 'MasterCategories.revenue',
        },
      ],
      income: [],
      masterCategory: [],
      profitAndLossData: [],
      selectedRange: 'today',
      iconSize: 40,
      subCategoryBody: null,
      goodsIncome: null,
      goodsCost: null,
      expand: false,
      reportsData: [],
      start: this.$moment().startOf('day'),
      end: this.$moment().endOf('day'),
      dateTime: {
        start: this.$moment().startOf('day').toISOString(),
        end: this.$moment().endOf('day').toISOString(),
      },
      loading: false,
      locationId: null,
      saleHeaders: [
        {
          text: this.$t('labels.name'),
          align: 'left',
          sortable: false,
          value: 'name',
        },
        {
          text: this.$t('labels.category'),
          value: 'category',
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('labels.quantity'),
          value: 'quantity',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('labels.cost'),
          value: 'cost',
          sortable: false,
        },
        {
          text: this.$t('labels.sale'),
          value: 'sale',
          sortable: false,
        },
      ],
      headers: [
        {
          text: this.$t('labels.name'),
          align: 'left',
          sortable: false,
          value: 'name',
        },
        {
          text: this.$t('labels.quantity'),
          value: 'quantity',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('labels.cost'),
          value: 'cost',
          sortable: false,
        },
      ],
    }
  },
  components: {
    Category,
    ReportViewBar,
    DateTimePicker,
  },
  async created() {
    const { start, end, range, LocationId } = this.$route.query
    this.locationId = LocationId
    if (start && end) {
      this.dateTime.start = start
      this.dateTime.end = end
    } else {
      this.dateTime.start = this.$moment().startOf('month').toISOString()
      this.dateTime.end = this.$moment().endOf('month').toISOString()
    }
    this.selectedRange = range;
    await this.loadReport()
  },
  computed: {
    incomeTotal() {
      let total = 0
      if (!this.income.InvoiceDetailTypes) return total
      this.income.InvoiceDetailTypes.map((row) => {
        total += parseFloat(row.revenue)
      })
      return total
    },
    costTotal() {
      let total = 0
      if (!this.income.InvoiceDetailTypes) return total
      this.income.InvoiceDetailTypes.map((row) => {
        total += parseFloat(row.cost)
      })
      return total
    },
    getDateTime() {
      return this.dateTime
    },
    categoryQuantityTotal() {
      let quantity = 0
      if (this.reportsData) {
        this.reportsData.saleByCategory.map((category) => {
          quantity += parseFloat(category.quantity)
        })
      }
      return quantity
    },
    categoryAmountTotal() {
      let amount = 0
      if (this.reportsData) {
        this.reportsData.saleByCategory.map((category) => {
          amount += parseFloat(category.total)
        })
      }
      return amount
    },
  },
  methods: {
    productName(val) {
      let name = null
      val.map((item) => {
        name = item
      })
      return name
    },
    subCategoryaName(val) {
      return val[0]
    },
    getProfitAndLoss() {
      const where = {
        start: this.dateTime.start,
        end: this.dateTime.end,
        LocationId: this.locationId,
        InvoiceDetailTypes: true,
      }
      reportService.getProfitAndLoss(where)
      .then((response) => {
        this.income = response.data;
      })
      .catch((error) => { 
        this.$Progress.fail();
        this.loading = false;
        throw error;
      })
    },
    async loadReport() {
      this.$Progress.start()
      this.loading = true
      await reportService.Location.daySummary({
        LocationId: this.locationId,
        start: this.dateTime.start,
        end: this.dateTime.end,
      })
        .then((response) => {
          this.$Progress.finish()
          this.reportsData = response.data
          this.loading = false

          this.goodsIncome = this.reportsData.profitAndLoss.report.Goods.Income
          this.goodsCost = this.reportsData.profitAndLoss.report.Goods.CostOfGoods

          Object.keys(this.goodsIncome.data).map(async (masterCategory) => {
            const masterCategoryData = this.goodsIncome.data[masterCategory]
            const masterCategoryCostData = this.goodsCost.data[masterCategory]

            await Promise.all(
              Object.keys(masterCategoryData.data).map(async (subCategory) => {
                const subCategoryData = masterCategoryData.data[subCategory]
                const subCategoryCostData = masterCategoryCostData.data[subCategory]

                if (Object.keys(subCategoryData.data).length > 0) {
                  const subCategoryBody = await Promise.all(
                    Object.keys(subCategoryData.data).map((lineItem) => {
                      const lineItemData = subCategoryData.data[lineItem]
                      const lineItemCostData = subCategoryCostData.data[lineItem]
                      return this.profitAndLossData.push({
                        name: lineItem,
                        quantity: lineItemData.quantity,
                        cost: lineItemCostData.total,
                        total: lineItemData.total,
                      })
                    }),
                  )
                } else {
                  this.subCategory.push({
                    name: subCategory,
                    quantity: subCategoryData.quantity,
                    cost: subCategoryCostData.total,
                    total: subCategoryData.total,
                  })
                }
              }),
            )
            if (masterCategoryData) {
              this.masterCategory.push({
                name: masterCategoryData,
                quantity: masterCategoryData.quantity,
                cost: masterCategoryCostData.total,
                total: masterCategoryData.total,
              })
            }
          })
        })
        .catch((error) => {
          this.$Progress.fail()
          this.loading = false
          throw error
        })

        await this.getProfitAndLoss();
    },
  },
}
</script>

<style>
</style>


